/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import {
	AfterContentInit,
	ChangeDetectionStrategy,
	Component,
	ContentChildren,
	HostBinding,
	OnDestroy,
	QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MultiPanelService } from '../multi-panel.service';
import { MultiPanelItemDirective } from '../multi-panel-item/multi-panel-item.directive';
import { combineLatest, defer, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

interface IPanelConfig {
	panel: MultiPanelItemDirective;
	panelId: string;
	collapsed: boolean;
	column: number;
	row: number;
}

//opted against the cdk-accordian
@Component({
	selector: 'tp-multi-panel',
	standalone: true,
	imports: [CommonModule],
	providers: [MultiPanelService],
	templateUrl: './multi-panel.component.html',
	styleUrls: ['./multi-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiPanelComponent implements AfterContentInit, OnDestroy {
	//https://angular.dev/guide/components/dom-apis#when-to-use-dom-apis
	// sadge
	// https://v17.angular.io/api/core/ContentChildren#tab-pane-example

	@HostBinding('style.grid-template-columns') protected gridTemplateColumns;
	@HostBinding('style.grid-template-rows') protected gridTemplateRows;
	//one of those sketchy "not an array but implements array methods" things
	@ContentChildren(MultiPanelItemDirective) protected panelDirectives: QueryList<MultiPanelItemDirective>;

	private destroy$ = new Subject<void>();

	constructor(private multiPanelService: MultiPanelService) {}

	public ngAfterContentInit(): void {
		const deferredPanel$: Observable<QueryList<MultiPanelItemDirective>> = defer(() =>
			this.panelDirectives.changes.pipe(startWith(this.panelDirectives))
		);
		combineLatest([deferredPanel$, this.multiPanelService.collapse$.pipe(startWith(new Map()))])
			.pipe(
				map(([panels, collapse]) => {
					//panels can be the authorative list
					return panels.map((panel) => {
						return {
							panel: panel,
							panelId: panel.id,
							collapsed: collapse.get(panel.id),
						};
					});
				}),
				map((panels) => this.layout(panels)),
				takeUntil(this.destroy$)
			)
			.subscribe((layout) => {
				this.applyCss(layout);
			});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// convert the layout to it's css equivalent
	private applyCss(layout: { gridLayout: [number, number]; panels: IPanelConfig[] }) {
		this.gridTemplateColumns = `repeat(${layout.gridLayout[0] + 1}, auto)`;
		this.gridTemplateRows = layout.gridLayout[1] ? `repeat(${layout.gridLayout[1]}, min-content) 1fr` : '1fr';

		// only necessary to set child layout if there are more than two panels
		// this is not "heavily" tested though
		layout.panels.forEach((panel) => {
			panel.panel.setLayout(panel.column, panel.column, panel.row, panel.row);
		});
	}

	//general algorithm
	// - is the previous panel collapsed, or the first, stack below. Else, stack to the right
	// - behaves pretty sensibly with contiguous collapsed panels, does little groups if they're not
	private layout(panels: { panelId: string; collapsed: boolean; panel: MultiPanelItemDirective }[]) {
		const newLayout: IPanelConfig[] = [];
		//consider changing to a map
		panels.forEach((panel, index) => {
			if (index === 0) {
				newLayout.push({ ...panel, column: 0, row: 0 });
				return;
			}
			const previous = newLayout[index - 1];
			if (previous.collapsed) {
				newLayout.push({ ...panel, column: previous.column, row: previous.row + 1 });
			} else {
				newLayout.push({ ...panel, column: previous.column + 1, row: 0 });
			}
		});

		const maxLayout: [number, number] = newLayout.reduce(
			(acc, panel) => {
				return [Math.max(acc[0], panel.column), Math.max(acc[1], panel.row)];
			},
			[0, 0]
		);

		return {
			gridLayout: maxLayout,
			panels: newLayout,
		};
	}
}
